import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/axiosInstance';
import { useStyles } from './CreatePersonaPage.styles';
import SelectTTSVoiceModal from '../layout/SelectTTSVoiceModal';
import socket from '../../services/socket';
import LoadingModal from '../layout/LoadingModal';
import { useDispatch } from 'react-redux';
import { appActions } from '../../actions/app.actions.js';
import { useTranslation } from 'react-i18next';
import backIconImg from '../../img/backIcon.svg';
import autogetnImg from '../../img/autogen.svg';
import defImg from '../../img/defimg.svg';

// 이미지 업로드 부분
const FileUpload = ({ onChange, accept }) => {
  const [uploadedImage, setUploadedImage] = useState(null);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: acceptedFiles => {
      const file = acceptedFiles[0];
      setUploadedImage(URL.createObjectURL(file));
      if (onChange) {
        onChange(acceptedFiles);
      }
    },
    accept,
    multiple: false,
  });

  const classes = useStyles();

  return (
    <div className={classes.dropzoneContainer}>
      <div {...getRootProps({ className: classes.dropzone })}>
        <input {...getInputProps()} />
        {uploadedImage ? (
          <img src={uploadedImage} alt="Uploaded" className={classes.uploadedImage} />
        ) : (
          <>
            <img src={defImg} alt="Img Icon" className={classes.imgIcon} />
            <p className={classes.uploadText}>업로드 하세요</p>
          </>
        )}
      </div>
    </div>
  );
};

const CreatePersonaPage = () => {
  const classes = useStyles();
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm();
  const dispatch = useDispatch();
  const { t } = useTranslation(); // useTranslation 훅 추가
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [error, setErrorState] = useState(null);
  const [image, setImage] = useState(null);
  const [ttsTrainingId, setTtsTrainingId] = useState(null);
  const [selectVoiceModalProps, setSelectVoiceModalProps] = useState({});
  const [shared, setShared] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isFormValid, setIsFormValid] = useState(false); // 폼의 유효성 상태
  const [text, setText] = useState('');
  const [dialogueText, setDialogueText] = useState('');
  const [isAutoGenerateDisabled, setIsAutoGenerateDisabled] = useState(false);

  const maxLengths = {
    modelName: 50,
    description: 50,
    instruction: 10000,
    dialogueStyle: 10000,
    charInfo: 10000,
  };

  const hasReachedMaxLength = field => watch(field)?.length >= maxLengths[field];
  // 폼 필드 값이 모두 입력되었는지 확인하는 useEffect
  
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      const allFieldsFilled =
        value.modelName?.trim() &&
        value.description?.trim() &&
        value.instruction?.trim() &&
        value.dialogueStyle?.trim() &&
        value.charInfo?.trim() &&
        image !== null &&
        ttsTrainingId !== null;
        setIsFormValid(allFieldsFilled); // 모든 필드가 채워지면 isFormValid를 true로 설정
    });
    return () => subscription.unsubscribe();
  }, [watch, image, ttsTrainingId]);
  
  
  useEffect(() => {
    socket.on('progress', progress => {
      let translatedMessage = t(progress.message);
      setLoadingMessage(translatedMessage);
    });
    return () => {
      socket.off('progress');
    };
  }, [navigate, image, ttsTrainingId]);

  // 페르소나 설정 및 성격 자동 생성을 위한 socket
  useEffect(() => {
    socket.on('connect', () => {
      console.log('Connected to server:', socket.id);
    });

    socket.on('chunk', data => {
      // 단일 chunk 이벤트 핸들링
      if (data.type === 'profile') {
        setText(prevText => prevText + data.message.value.replace(/【.*?】/g, ''));
        clearErrors('instruction');
      } else if (data.type === 'dialogue') {
        setDialogueText(prevText => prevText + data.message.value.replace(/【.*?】/g, ''));
        clearErrors('dialogueStyle');
      }
    });

    socket.on('end', data => {
      const text = data.message;
      if (data.type === 'profile') {
        setValue('instruction', text, { shouldValidate: true });
        setIsAutoGenerateDisabled(false); // 프로세스 끝나면 버튼 다시 활성화
      } else if (data.type === 'dialogue') {
        setValue('dialogueStyle', text, { shouldValidate: true });
        setIsAutoGenerateDisabled(false); // 프로세스 끝나면 버튼 다시 활성화
      }
    });

    socket.on('disconnect', () => {
      setErrorState(t('personaCreateTab.error'));
      dispatch(appActions.openMessageModal(t('historyTab.modal.error'), false, true));
    });

    return () => {
      socket.off('connect');
      socket.off('chunk');
      socket.off('end');
    };
  }, [text, dialogueText, clearErrors, setValue]);

  // 이미지 업로드
  const handleImageUpload = files => {
    clearErrors('image');
    setImage(files[0]);
  };

  // TTS training ID 선택
  const handleSelectVoice = () => {
    setSelectVoiceModalProps({
      open: true,
      onSelectVoice: selectedVoice => {
        clearErrors('ttsTrainingId');
        setTtsTrainingId(selectedVoice);
        setSelectVoiceModalProps({ open: false });
      },
      onSelectVoiceModalClose: () => {
        clearErrors('ttsTrainingId');
        setSelectVoiceModalProps({ open: false });
      },
    });
  };

  // 성격 자동 생성
  const handleAutoGenInstruction = () => {
    const description = watch('description');
    if (!description) {
      setError('description', {
        type: 'manual',
        message: t('personaCreateTab.noInput'),
      });
      return;
    }
    clearErrors('description');
    setText('');
    setIsAutoGenerateDisabled(true); // 버튼 비활성화
    socket.emit('generate', { text: description, type: 'profile', language: currentLanguage }); // 타입을 포함하여 emit
  };

  // 대화 예시 자동 생성
  const handleAutoGenDialogue = () => {
    if (!text) {
      setError('instruction', {
        type: 'manual',
        message: t('personaCreateTab.noInput'),
      });
      return;
    }
    clearErrors('instruction');
    setDialogueText('');
    setIsAutoGenerateDisabled(true); // 버튼 비활성화
    socket.emit('generate', { text, type: 'dialogue', language: currentLanguage });
  };

  // 챗봇 생성하기 버튼 눌렀을 때
  const onSubmit = async data => {
    let hasError = false;
    if (!image) {
      setError('image', { type: 'manual', message: t('personaCreateTab.noInput') });
      hasError = true;
    }
    if (!ttsTrainingId) {
      setError('ttsTrainingId', { type: 'manual', message: t('personaCreateTab.noInput') });
      hasError = true;
    }
    if (hasError) {
      return;
    }
    setLoading(true);
    setSubmitting(true);

    const formData = new FormData();
    if (image) {
      formData.append('image', image);
      console.log('image', image);
    }

    const jsonData = JSON.stringify({
      name: data.modelName,
      description: data.description,
      charInfo: data.charInfo,
      TtsTrainingId: ttsTrainingId.id,
      shared: shared,
      instruction: data.instruction,
      dialogueStyle: data.dialogueStyle,
      socketId: socket.id,
    });
    console.log('JsonData:', jsonData);

    formData.append('data', jsonData);
    try {
      const response = await axiosInstance.post(`/assistants`, formData);
      const assistantData = response.data.data;

      navigate('/chat', {
        state: {
          modelName: data.modelName,
          instructions: data.instructions,
          image: image ? URL.createObjectURL(image) : null,
          assistantId: assistantData.id,
        },
      });
    } catch (error) {
      console.error('Error creating assistant:', error);
      dispatch(appActions.openMessageModal(t('historyTab.modal.error'), false, true));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.main}>
      <div className={classes.headerContainer}>
        <button onClick={() => navigate(-1)} className={classes.backButton}>
          <img src={backIconImg} alt="Img Icon" className={classes.backIcon} />
          {t('personaCreateTab.back')}
        </button>
        <button type="submit" form="createPersonaForm" className={isFormValid ? classes.submitButtonActive : classes.submitButton} disabled={submitting}>
        {t('personaCreateTab.create')}
        </button>
      </div>
      <div className={classes.titleContainer}>
        <h2 className={classes.title}>{t('personaCreateTab.title')}</h2>
      </div>
      <div className={classes.container}>
        <LoadingModal open={loading} message={loadingMessage} />
        <SelectTTSVoiceModal {...selectVoiceModalProps} />

        <form id="createPersonaForm" onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div className={classes.formGroup} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',  width: '20%'}}>
              <Controller
                name="image"
                control={control}
                render={({ field }) => <FileUpload {...field} onChange={handleImageUpload} accept="image/*" />}
              />
              {errors.image && <p className={classes.errorText} style={{ textAlign: 'center' }}>{errors.image.message}</p>}    
            </div>
            <div className={classes.inputContainer}>
              <label className={classes.label}>{t('personaCreateTab.name')}</label>
              <Controller
                name="modelName"
                control={control}
                rules={{
                  required: t('personaCreateTab.noInput'),
                  maxLength: {
                    value: maxLengths.modelName
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    placeholder={t('personaCreateTab.namePlaceholder')}
                    className={classes.input}
                    maxLength={maxLengths.modelName}
                  />
                )}
              />
              <div className={classes.charCountContainer}>
                <span>
                  {watch('modelName')?.length || 0}/{maxLengths.modelName}
                </span>
              </div>
              {hasReachedMaxLength('modelName') && (
                <p className={classes.errorText} style={{ color: 'red' }}>
                  {t('personaCreateTab.exceedTextSize')}
                </p>
              )}
              {errors.modelName && <p className={classes.errorText}>{errors.modelName.message}</p>}
            </div>
          </div>

          <div className={classes.ttsGroup}>
            <label className={classes.lable}>{t('personaCreateTab.ttsModel')}</label>
            <div style={{ flexDirection: 'column'}}>
            <Controller
              name="TTSVoiceModel"
              control={control}
              render={({ field }) => (
                <button type="button" onClick={handleSelectVoice} className={classes.selectButton}>
                  {ttsTrainingId ? ttsTrainingId.label : t('personaCreateTab.ttsModel')}
                </button>
              )}
            />
            {errors.ttsTrainingId && <p className={classes.errorText}style={{ textAlign: 'right' }}>{errors.ttsTrainingId.message}</p>}
            </div>
          </div>

          <div className={classes.textContainer}>
            <label className={classes.label} style={{ marginTop: '1em' }}>
              {t('personaCreateTab.description')}{' '}
            </label>
            <Controller
              name="description"
              control={control}
              rules={{
                required: t('personaCreateTab.noInput'),
                maxLength: {
                  value: maxLengths.description
                },
              }}
              render={({ field }) => (
                <textarea
                  {...field}
                  placeholder={t('personaCreateTab.descriptionPlaceholder')}
                  className={classes.inputDescription}
                  maxLength={maxLengths.description}
                />
              )}
            />
            <div className={classes.charCountContainer}>
              <span>
                {watch('description')?.length || 0}/{maxLengths.description}
              </span>
            </div>
            {hasReachedMaxLength('description') && (
              <p className={classes.errorText} style={{ color: 'red' }}>
                {t('personaCreateTab.exceedTextSize')}
              </p>
            )}
            {errors.description && <p className={classes.errorText}>{errors.description.message}</p>}
          </div>
          <div className={classes.textContainer}>
            <div className={classes.formGroup}>
              <div className={classes.formRowSpaceBetween}>
                <label className={classes.label}>{t('personaCreateTab.character')}</label>
                <button
                  type="button"
                  onClick={handleAutoGenInstruction}
                  className={`${classes.autoGenerateButton} ${isAutoGenerateDisabled ? classes.disabledButton : ''}`}
                  disabled={isAutoGenerateDisabled}
                >
                  <img src={autogetnImg} alt="Img Icon" className={classes.imgIcon} />
                  {t('personaCreateTab.autoGenerate')}
                </button>
              </div>
              <Controller
                name="instruction"
                control={control}
                rules={{
                  required: t('personaCreateTab.noInput'),
                  maxLength: {
                    value: maxLengths.instruction
                  },
                }}
                render={({ field }) => (
                  <textarea
                    {...field}
                    value={text}
                    onChange={e => {
                      setText(e.target.value);
                      setValue('instruction', e.target.value, { shouldValidate: true });
                    }}
                    placeholder={t('personaCreateTab.characterPlaceholder')}
                    className={classes.textarea}
                    maxLength={maxLengths.instruction}
                  />
                )}
              />
              <div className={classes.charCountContainer}>
                <span>
                  {watch('instruction')?.length || 0}/{maxLengths.instruction}
                </span>
              </div>
              {hasReachedMaxLength('instruction') && (
                <p className={classes.errorText} style={{ color: 'red' }}>
                  {t('personaCreateTab.exceedTextSize')}
                </p>
              )}
              {errors.instruction && <p className={classes.errorText}>{errors.instruction.message}</p>}
            </div>
          </div>
          <div className={classes.textContainer}>
            <div className={classes.formGroup}>
              <div className={classes.formRowSpaceBetween}>
                <label className={classes.label}>{t('personaCreateTab.dialogue')} </label>
                <button
                  type="button"
                  onClick={handleAutoGenDialogue}
                  className={`${classes.autoGenerateButton} ${isAutoGenerateDisabled ? classes.disabledButton : ''}`}
                  disabled={isAutoGenerateDisabled}
                >
                  <img src={autogetnImg} alt="Img Icon" className={classes.imgIcon} />
                  {t('personaCreateTab.autoGenerate')}
                </button>
              </div>
              <Controller
                name="dialogueStyle"
                control={control}
                rules={{
                  required: t('personaCreateTab.noInput'),
                  maxLength: {
                    value: maxLengths.dialogueStyle
                  },
                }}
                render={({ field }) => (
                  <textarea
                    {...field}
                    value={dialogueText}
                    onChange={e => {
                      setDialogueText(e.target.value);
                      setValue('dialogueStyle', e.target.value, { shouldValidate: true });
                    }}
                    placeholder= {t('personaCreateTab.dialoguePlaceholder')}
                    className={classes.textarea}
                    maxLength={maxLengths.dialogueStyle}
                  />
                )}
              />
              <div className={classes.charCountContainer}>
                <span>
                  {watch('dialogueStyle')?.length || 0}/{maxLengths.dialogueStyle}
                </span>
              </div>
              {hasReachedMaxLength('dialogueStyle') && (
                <p className={classes.errorText} style={{ color: 'red' }}>
                  {t('personaCreateTab.exceedTextSize')}
                </p>
              )}
              {errors.dialogueStyle && <p className={classes.errorText}>{errors.dialogueStyle.message}</p>}
            </div>
          </div>
          <div className={classes.textContainer}>
            <div className={classes.formGroup}>
              <label className={classes.label}>{t('personaCreateTab.wiki')} </label>
              <Controller
                name="charInfo"
                control={control}
                rules={{
                  required: t('personaCreateTab.noInput'),
                  maxLength: {
                    value: maxLengths.charInfo
                  },
                }}
                render={({ field }) => (
                  <textarea
                    {...field}
                    placeholder={t('personaCreateTab.wikiPlaceholder')}
                    className={classes.textarea}
                    maxLength={maxLengths.charInfo}
                  />
                )}
              />
              <div className={classes.charCountContainer}>
                <span>
                  {watch('charInfo')?.length || 0}/{maxLengths.charInfo}
                </span>
              </div>
              {hasReachedMaxLength('charInfo') && (
                <p className={classes.errorText} style={{ color: 'red' }}>
                  {t('personaCreateTab.exceedTextSize')}
                </p>
              )}
              {errors.charInfo && <p className={classes.errorText}>{errors.charInfo.message}</p>}
            </div>
          </div>
          <div className={classes.sharedGroup}>
            <label className={classes.label}>{t('personaCreateTab.communityVisibilityStatus')}</label>
            <div className={classes.toggleContainer}>
              <div className={classes.toggleButtonWrapper}>
                <div className={`${classes.toggleButton} ${shared ? 'active' : ''}`} onClick={() => setShared(true)}>
                  {t('personaCreateTab.public')}
                </div>
                <div className={`${classes.toggleButton} ${!shared ? 'active' : ''}`} onClick={() => setShared(false)}>
                  {t('personaCreateTab.private')}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePersonaPage;
